import { useState } from 'react';
import type { TaskRow } from '../api/types';
import { TaskItem } from './TaskItem';
import { QuickAddTask } from './QuickAddTask';
import { SyncIndicator } from './SyncIndicator';

interface Props {
  title: string;
  subtitle?: string;
  tasks: TaskRow[];
  loading: boolean;
  error: string | null;
  selectedTaskId: string | null;
  onSelectTask: (id: string) => void;
  onToggleComplete: (task: TaskRow) => void;
  onToggleStarred?: (task: TaskRow) => void;
  onLongPress?: (task: TaskRow, rect: DOMRect) => void;
  onRefresh: () => void;
  // QuickAdd 仅 list 视图显示
  showQuickAdd?: { listId: string; onCreated: () => void };
  /** 移动端: 打开侧边栏抽屉 */
  onOpenSidebar?: () => void;
  /** 已完成视图: 按完成时间分组 (今天/本周/本月/更早) */
  groupByCompletedTime?: boolean;
}

export function TaskList({
  title,
  subtitle,
  tasks,
  loading,
  error,
  selectedTaskId,
  onSelectTask,
  onToggleComplete,
  onToggleStarred,
  onLongPress,
  onRefresh,
  showQuickAdd,
  onOpenSidebar,
  groupByCompletedTime,
}: Props) {
  const groups = groupByCompletedTime
    ? groupTasksByCompletedTime(tasks)
    : null;
  return (
    <div className="flex h-full flex-col">
      <header className="header-immersive sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 px-3 py-2 sm:px-6 sm:py-3 dark:border-gray-700 safe-top">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* 移动端汉堡按钮 */}
          <button
            onClick={onOpenSidebar}
            className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-100 md:hidden dark:hover:bg-gray-700"
            aria-label="打开侧边栏"
            title="侧边栏"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm1 4a1 1 0 100 2h12a1 1 0 100-2H4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold sm:text-lg">{title}</h2>
            {subtitle && (
              <p className="truncate text-xs text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <SyncIndicator />
          <button
            onClick={onRefresh}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            title="刷新"
          >
            ↻
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-2 safe-bottom">
        {error && (
          <div className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        )}
        {loading && tasks.length === 0 ? (
          <p className="p-4 text-center text-sm text-gray-400">加载中...</p>
        ) : tasks.length === 0 ? (
          <p className="p-4 text-center text-sm text-gray-400">没有任务</p>
        ) : groups ? (
          <GroupedTaskList
            groups={groups}
            selectedTaskId={selectedTaskId}
            onSelectTask={onSelectTask}
            onToggleComplete={onToggleComplete}
            onToggleStarred={onToggleStarred}
            onLongPress={onLongPress}
          />
        ) : (
          <div className="space-y-0.5">
            {tasks.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                selected={selectedTaskId === t.id}
                onClick={() => onSelectTask(t.id)}
                onToggleComplete={() => onToggleComplete(t)}
                onToggleStarred={
                  onToggleStarred ? () => onToggleStarred(t) : undefined
                }
                onLongPress={onLongPress ? (rect) => onLongPress(t, rect) : undefined}
              />
            ))}
          </div>
        )}

        {showQuickAdd && (
          <div className="mt-2">
            <QuickAddTask listId={showQuickAdd.listId} onCreated={showQuickAdd.onCreated} />
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================
// 按完成时间分组
// =====================================================
const ONE_DAY = 24 * 60 * 60 * 1000;

export interface TaskGroup {
  key: string;
  label: string;
  tasks: TaskRow[];
}

export function groupTasksByCompletedTime(tasks: TaskRow[]): TaskGroup[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayStart = startOfToday.getTime();
  const weekStart = todayStart - 7 * ONE_DAY;
  const monthStart = todayStart - 30 * ONE_DAY;

  const today: TaskRow[] = [];
  const week: TaskRow[] = [];
  const month: TaskRow[] = [];
  const older: TaskRow[] = [];

  for (const t of tasks) {
    const ts = t.completed_at ?? t.updated_at ?? 0;
    if (ts >= todayStart) today.push(t);
    else if (ts >= weekStart) week.push(t);
    else if (ts >= monthStart) month.push(t);
    else older.push(t);
  }

  const groups: TaskGroup[] = [];
  if (today.length > 0) groups.push({ key: 'today', label: '今天', tasks: today });
  if (week.length > 0) groups.push({ key: 'week', label: '本周', tasks: week });
  if (month.length > 0) groups.push({ key: 'month', label: '本月', tasks: month });
  if (older.length > 0) groups.push({ key: 'older', label: '更早', tasks: older });
  return groups;
}

// =====================================================
// 分组渲染 (可折叠, "更早"默认折叠)
// =====================================================
interface GroupedTaskListProps {
  groups: TaskGroup[];
  selectedTaskId: string | null;
  onSelectTask: (id: string) => void;
  onToggleComplete: (task: TaskRow) => void;
  onToggleStarred?: (task: TaskRow) => void;
  onLongPress?: (task: TaskRow, rect: DOMRect) => void;
}

function GroupedTaskList({
  groups,
  selectedTaskId,
  onSelectTask,
  onToggleComplete,
  onToggleStarred,
  onLongPress,
}: GroupedTaskListProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(groups.filter((g) => g.key === 'older').map((g) => g.key))
  );
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.key}>
          <button
            onClick={() => toggle(g.key)}
            className="fx-tap flex w-full items-center justify-between px-2 py-1 text-xs font-medium uppercase text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <span>
              {g.label} · {g.tasks.length}
            </span>
            <span className="text-base">{collapsed.has(g.key) ? '▸' : '▾'}</span>
          </button>
          {!collapsed.has(g.key) && (
            <div className="space-y-0.5">
              {g.tasks.map((t) => (
                <TaskItem
                  key={t.id}
                  task={t}
                  selected={selectedTaskId === t.id}
                  onClick={() => onSelectTask(t.id)}
                  onToggleComplete={() => onToggleComplete(t)}
                  onToggleStarred={
                    onToggleStarred ? () => onToggleStarred(t) : undefined
                  }
                  onLongPress={onLongPress ? (rect) => onLongPress(t, rect) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}